import { useEffect, useMemo, useCallback } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  Box,
  Grid,
} from "@mui/material";
import {
  add,
  differenceInHours,
  differenceInMinutes,
  startOfHour,
} from "date-fns";
import { RescheduledTime } from "./ProductCard";
import { useContext } from "react";
import { AppContext } from "../contextProviders/AppContext";
import { translation } from "./Dictionary";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { getPriceByVehicleIds } from "../graphql/queries";
import { captureError } from "../utilities";
export default function ResultTimeSelector({
  freeTimes,
  startTime,
  returnTime,
  requestedStartTime,
  suggestedStartTime,
  suggestedReturnTime,
  useVehicleTimes,
  disableOffHourReturn,
  company,
  setRescheduleOfferReturn,
  setRescheduleOfferStart,
  offer,
  setPriceLoading,
  setOffer,
}) {
  const theme = useTheme();
  const { mobileViewPort, language } = useContext(AppContext);
  const FEATURE_STARTTIME_LABEL = useMemo(() => {
    if (company?.group === "gnista") {
      return translation.get(language, "Daterange check-in time");
    } else {
      translation.get(language, "Pickup time text");
    }
  }, [company?.group, language]);

  const FEATURE_RETURNTIME_LABEL = useMemo(() => {
    if (company?.group === "gnista") {
      return translation.get(language, "Daterange check-out time");
    } else {
      translation.get(language, "Return time text");
    }
  }, [company?.group, language]);

  const onTimeChange = useCallback(
    async (start, end) => {
      if (start) {
        setRescheduleOfferStart(start);
      }
      if (end) {
        setRescheduleOfferReturn(end);
      }
      if (start || end) {
        setPriceLoading(true);
        const response = await fetchPrice(
          start || startTime,
          end || returnTime,
          offer
        );
        if (response) {
          setOffer((prevState) => ({
            ...prevState,
            additionalServiceOffers: response.additionalServiceOffers,
            vehicleOfferPrices: response.vehicleOfferPrices,
            vehicleOfferPrice: response.vehicleOfferPrices[0]?.price,
          }));
          setPriceLoading(false);
        }
      }
    },
    [startTime, returnTime, offer]
  );

  const startTimes = useMemo(() => {
    const times = [];
    if (useVehicleTimes) {
      const timesInMs = freeTimes.map((time) => time.firstStartTime.getTime());
      const timesInMsNoDups = [...new Set(timesInMs)];
      const dateTimes = timesInMsNoDups.map((ms) => new Date(ms));
      for (const date of dateTimes) {
        times.push(date);
      }
    } else if (disableOffHourReturn) {
      freeTimes.forEach((freeTime) => {
        const earliest = freeTime.firstStartTime;
        const length = differenceInHours(freeTime.lastStartTime, earliest);

        for (let i = 0; i < length + 1; i++) {
          times.push(add(earliest, { hours: i }));
        }
      });
    } else {
      freeTimes.forEach((freeTime) => {
        const earliest =
          freeTime.firstStartTime.getMinutes() === 0
            ? freeTime.firstStartTime
            : startOfHour(add(freeTime.firstStartTime, { hours: 1 }));
        const length = differenceInHours(freeTime.lastStartTime, earliest);

        for (let i = 0; i < length + 1; i++) {
          times.push(add(earliest, { hours: i }));
        }
      });
    }

    if (
      !times.find((time) => time.getTime() === suggestedStartTime.getTime())
    ) {
      times.unshift(suggestedStartTime);
    }

    return times;
  }, [freeTimes, suggestedStartTime, useVehicleTimes]);

  const endTimes = useMemo(() => {
    const times = [];

    if (useVehicleTimes) {
      for (const time of freeTimes) {
        if (startTime.getTime() === time.firstStartTime.getTime()) {
          times.push(time.lastReturnTime);
        }
      }
      return times;
    }

    if (disableOffHourReturn) {
      const freeTime = freeTimes.find(
        (freeTime) =>
          freeTime.firstStartTime <= startTime &&
          freeTime.lastStartTime >= startTime
      );
      for (let returnTimes of freeTime.returnTimes) {
        const earliest = returnTimes.start;
        const length = differenceInHours(returnTimes.end, returnTimes.start);

        for (let i = 0; i <= length; i++) {
          const lastTime = add(earliest, { hours: i });
          if (lastTime < startTime) continue;
          times.push(lastTime);
          if (i < length) {
            times.push(add(lastTime, { minutes: 30 }));
          }
        }
      }
      const timesInMs = times.map((time) => time.getTime());
      const timesInMsNoDups = [...new Set(timesInMs)];
      const dateTimes = timesInMsNoDups.map((time) => new Date(time));
      return dateTimes;
    }

    const freeTime = freeTimes.find(
      (freeTime) =>
        freeTime.firstStartTime <= startTime &&
        freeTime.lastReturnTime >= startTime
    );

    if (!freeTime) return times;

    let suggestedLength = differenceInHours(
      suggestedReturnTime,
      suggestedStartTime
    );
    const length = differenceInHours(freeTime.lastReturnTime, startTime);
    let from = Math.max(0, suggestedLength - 5);
    let to = Math.min(length, suggestedLength + 6);
    if (company.disableOffHourReturn) {
      // Times that are outside of business hours will be ignored
      // To ensure sufficient amount of options, try times until last possible
      const toDate = add(startTime, { hours: to });
      const differenceToLast = Math.abs(
        differenceInHours(toDate, freeTime.lastReturnTime)
      );
      to = to + differenceToLast;
    }

    for (let i = from; i < to; i++) {
      let businessHours;
      const newTime = add(startTime, { hours: i + 1 });
      times.push(newTime);
      // add a :30 to all but last
      if (i !== length - 1) {
        const lastTime = add(startTime, { hours: i + 1, minutes: 30 });
        if (company.disableOffHourReturn && !(lastTime <= businessHours?.end))
          continue;

        times.push(lastTime);
      }
    }

    return times;
  }, [freeTimes, startTime, suggestedStartTime, suggestedReturnTime]);

  useEffect(() => {
    if (
      !endTimes.find((endTime) => endTime.getTime() === returnTime.getTime())
    ) {
      onTimeChange(null, endTimes[endTimes.length - 1]);
    }
  }, [endTimes, returnTime, onTimeChange]);

  const minuteDiff = useMemo(
    () => differenceInMinutes(returnTime, startTime),
    [returnTime, startTime]
  );

  // const diffString = useMemo(() => {
  //   const h = Math.floor(minuteDiff / 60);
  //   const m = Math.floor(minuteDiff - h * 60);

  //   if (m === 0) {
  //     return h + " h";
  //   }
  //   return `${h}:${m}h`;
  // }, [minuteDiff]);

  const fetchPrice = async (start, end, offer) => {
    if (start >= end) return;

    try {
      const response = await API.graphql(
        graphqlOperation(getPriceByVehicleIds, {
          vehicleIds: [offer.vehicle.id],
          startTime: start.toJSON(),
          returnTime: end.toJSON(),
          companyId: offer.vehicle.companyId,
        })
      );
      const result = response.data.getPriceByVehicleIds;

      return result;
    } catch (e) {
      captureError("Get reservation price failed", "GET_PRICE_FAILED", e);
      console.error("fetchPrice", e);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        display: "flex",
        flexDirection: "row",

        alignItems: "center",
        justifyContent: "space-evenly",
        marginY: "5px",
        /*      background: theme.palette.primary.productCardBg,
        margin: "8px -16px 0",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderLeft: 0,
        borderRight: 0, */
        //    padding: 8,
      }}
    >
      <Grid
        item
        xs={6}
        md={4}
        sx={{
          maxWidth: "210px",
        }}
      >
        <FormControl
          fullWidth
          variant="outlined"
          style={{
            color: theme.palette.primary.mainText,
            backgroundColor: theme.palette.primary.providerSelectBg,
          }}
        >
          <InputLabel
            style={{
              color: theme.palette.primary.mainText,
            }}
          >
            {FEATURE_STARTTIME_LABEL}
          </InputLabel>
          <Select
            label={FEATURE_STARTTIME_LABEL}
            sx={{
              color: theme.palette.primary.mainText,
              backgroundColor: theme.palette.primary.providerSelectBg,
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "25vh",
                  borderBottomLeftRadius: "14px",
                  borderBottomRightRadius: "14px",

                  "& .MuiMenuItem-root": {
                    padding: 1,
                  },
                },
              },
            }}
            placeholder=""
            value={startTime.getTime()}
            onChange={(e) => {
              if (useVehicleTimes) {
                const date = new Date(e.target.value);
                onTimeChange(date, null);
              } else {
                const date = new Date(e.target.value);
                onTimeChange(date, add(date, { minutes: minuteDiff }));
              }
            }}
            displayEmpty
            variant="outlined"
            margin="dense"
          >
            {startTimes.map((startTime, idx) => (
              <MenuItem value={startTime.getTime()} key={idx}>
                <RescheduledTime
                  date={startTime}
                  requestedDate={requestedStartTime}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} md={4} sx={{ maxWidth: "210px" }}>
        <FormControl
          fullWidth
          variant="outlined"
          style={{
            color: theme.palette.primary.mainText,
            backgroundColor: theme.palette.primary.providerSelectBg,
          }}
        >
          <InputLabel
            style={{
              color: theme.palette.primary.mainText,
            }}
          >
            {FEATURE_RETURNTIME_LABEL}
          </InputLabel>
          <Select
            label={FEATURE_RETURNTIME_LABEL}
            sx={{
              color: theme.palette.primary.mainText,
              backgroundColor: theme.palette.primary.providerSelectBg,
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "25vh",
                  borderBottomLeftRadius: "14px",
                  borderBottomRightRadius: "14px",

                  "& .MuiMenuItem-root": {
                    padding: 1,
                  },
                },
              },
            }}
            placeholder=""
            value={returnTime.getTime()}
            onChange={(e) => {
              const date = new Date(e.target.value);
              onTimeChange(null, date);
            }}
            displayEmpty
            variant="outlined"
            margin="dense"
          >
            {endTimes.map((endTime, idx) => (
              <MenuItem value={endTime.getTime()} key={idx}>
                <RescheduledTime
                  date={endTime}
                  requestedDate={requestedStartTime}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}
